/**
 * Leviathan Web - Inventory Form Component
 * Quasar Prime LLC
 * Reusable form for creating and editing inventory items
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { API_URL } from '@/config/api';

interface InventoryFormData {
  name: string;
  sku: string;
  category: 'FUEL' | 'ADDITIVE' | 'LUBRICANT' | 'EQUIPMENT' | 'SERVICE' | 'OTHER';
  description: string;
  productType: string;
  grade: string;
  unit: string;
  costPerUnit: string;
  pricePerUnit: string;
  currentStock: string;
  reorderPoint: string;
  reorderQuantity: string;
  isActive: boolean;
  isTracked: boolean;
  notes: string;
}

interface InventoryFormProps {
  itemId?: string;
  initialData?: Partial<InventoryFormData>;
  onSuccess?: () => void;
}

export default function InventoryForm({ itemId, initialData, onSuccess }: InventoryFormProps) {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<InventoryFormData>({
    name: '',
    sku: '',
    category: 'FUEL',
    description: '',
    productType: '',
    grade: '',
    unit: 'gallon',
    costPerUnit: '',
    pricePerUnit: '',
    currentStock: '0',
    reorderPoint: '',
    reorderQuantity: '',
    isActive: true,
    isTracked: true,
    notes: '',
    ...initialData,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Build payload, converting string numbers to actual numbers
      const payload: Record<string, unknown> = {
        ...formData,
        costPerUnit: formData.costPerUnit ? parseFloat(formData.costPerUnit) : null,
        pricePerUnit: formData.pricePerUnit ? parseFloat(formData.pricePerUnit) : null,
        currentStock: formData.currentStock ? parseFloat(formData.currentStock) : 0,
        reorderPoint: formData.reorderPoint ? parseFloat(formData.reorderPoint) : null,
        reorderQuantity: formData.reorderQuantity ? parseFloat(formData.reorderQuantity) : null,
      };

      // Remove empty strings, convert to null
      Object.keys(payload).forEach((key) => {
        if (payload[key] === '') {
          payload[key] = null;
        }
      });

      const url = itemId
        ? `${API_URL}/api/inventory/${itemId}`
        : `${API_URL}/api/inventory`;

      const method = itemId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/inventory');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save inventory item');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-error-red/10 border border-error-red/50">
          <p className="text-error-red text-sm font-mono">&gt; Error: {error}</p>
        </div>
      )}

      {/* Basic Information */}
      <FormSection title="Product Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Product Name" required>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Ultra Low Sulfur Diesel"
              className="form-input"
            />
          </FormField>

          <FormField label="SKU">
            <input
              type="text"
              name="sku"
              value={formData.sku}
              onChange={handleChange}
              placeholder="Auto-generated if empty"
              className="form-input"
            />
          </FormField>

          <FormField label="Category" required>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className="form-input"
            >
              <option value="FUEL">Fuel</option>
              <option value="ADDITIVE">Additive</option>
              <option value="LUBRICANT">Lubricant</option>
              <option value="EQUIPMENT">Equipment</option>
              <option value="SERVICE">Service</option>
              <option value="OTHER">Other</option>
            </select>
          </FormField>

          <FormField label="Product Type">
            <input
              type="text"
              name="productType"
              value={formData.productType}
              onChange={handleChange}
              placeholder="e.g., #2 Diesel, Regular Unleaded"
              className="form-input"
            />
          </FormField>

          <FormField label="Grade">
            <input
              type="text"
              name="grade"
              value={formData.grade}
              onChange={handleChange}
              placeholder="e.g., Premium, Standard"
              className="form-input"
            />
          </FormField>

          <FormField label="Unit of Measure" required>
            <select
              name="unit"
              value={formData.unit}
              onChange={handleChange}
              required
              className="form-input"
            >
              <option value="gallon">Gallon</option>
              <option value="liter">Liter</option>
              <option value="quart">Quart</option>
              <option value="barrel">Barrel</option>
              <option value="unit">Unit</option>
              <option value="case">Case</option>
              <option value="each">Each</option>
            </select>
          </FormField>

          <div className="md:col-span-2">
            <FormField label="Description">
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={2}
                placeholder="Product description..."
                className="form-input resize-none"
              />
            </FormField>
          </div>
        </div>
      </FormSection>

      {/* Pricing */}
      <FormSection title="Pricing">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Cost Per Unit ($)">
            <input
              type="number"
              name="costPerUnit"
              value={formData.costPerUnit}
              onChange={handleChange}
              step="0.0001"
              min="0"
              placeholder="0.0000"
              className="form-input"
            />
            <p className="text-text-tertiary text-xs font-mono mt-1">What you pay suppliers</p>
          </FormField>

          <FormField label="Price Per Unit ($)">
            <input
              type="number"
              name="pricePerUnit"
              value={formData.pricePerUnit}
              onChange={handleChange}
              step="0.0001"
              min="0"
              placeholder="0.0000"
              className="form-input"
            />
            <p className="text-text-tertiary text-xs font-mono mt-1">Base selling price</p>
          </FormField>
        </div>
      </FormSection>

      {/* Inventory Tracking */}
      <FormSection title="Inventory Tracking">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField label="Current Stock">
            <input
              type="number"
              name="currentStock"
              value={formData.currentStock}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="0.00"
              className="form-input"
            />
          </FormField>

          <FormField label="Reorder Point">
            <input
              type="number"
              name="reorderPoint"
              value={formData.reorderPoint}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="Alert when stock falls below"
              className="form-input"
            />
          </FormField>

          <FormField label="Reorder Quantity">
            <input
              type="number"
              name="reorderQuantity"
              value={formData.reorderQuantity}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="Suggested reorder amount"
              className="form-input"
            />
          </FormField>
        </div>

        <div className="flex items-center space-x-8 mt-6">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              name="isTracked"
              checked={formData.isTracked}
              onChange={handleChange}
              className="w-5 h-5 text-amber bg-background border-panel-border focus:ring-amber rounded"
            />
            <span className="text-text-primary font-mono text-sm">Track Inventory Levels</span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="w-5 h-5 text-amber bg-background border-panel-border focus:ring-amber rounded"
            />
            <span className="text-text-primary font-mono text-sm">Active Product</span>
          </label>
        </div>
      </FormSection>

      {/* Notes */}
      <FormSection title="Notes">
        <FormField label="Internal Notes">
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            placeholder="Internal notes about this product..."
            className="form-input resize-none"
          />
        </FormField>
      </FormSection>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-4 pt-4">
        <button
          type="button"
          onClick={() => navigate('/inventory')}
          className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all border border-panel-border hover:border-steel"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={isLoading}
          className="px-8 py-3 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed relative group"
          style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
        >
          <span className="relative z-10">
            {isLoading ? 'Saving...' : itemId ? 'Update Product' : 'Create Product'}
          </span>
          <div
            className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ mixBlendMode: 'overlay' }}
          />
        </button>
      </div>
    </form>
  );
}

/**
 * Form section with geometric panel styling
 */
function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-6">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Form field with label
 */
function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
        {label} {required && <span className="text-amber">*</span>}
      </label>
      {children}
    </div>
  );
}
